/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Typography } from 'antd';

import { ValidationError } from '@/components/Forms/ValidationError';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { redirectUriPattern } from '@/pages/OAuthClients/redirectUriValidation';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';

const { Title } = Typography;

export function RedirectUrisSection() {
  const { t } = useMimicTranslation('oauthClients');

  return (
    <Card>
      <Container gap="md">
        <Title level={4} style={{ margin: 0 }}>
          {t('clientForm.redirectUrisTitle')}
        </Title>

        <div style={{ width: '100%' }}>
          <Form.List
            name="redirectUris"
            rules={[
              {
                validator: async (_, redirectUris) => {
                  if (!redirectUris || redirectUris.length < 1) {
                    return Promise.reject(new Error(t('clientForm.redirectUrisRequired')));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field, index) => (
                  <Form.Item
                    label={
                      index === 0 ? (
                        <Text style={{ fontWeight: '600' }}>{t('clientForm.redirectUrisLabel')}</Text>
                      ) : undefined
                    }
                    required={false}
                    key={field.key}
                    style={{ marginBottom: '12px' }}
                  >
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Form.Item
                        {...field}
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                          {
                            required: true,
                            whitespace: true,
                            message: <ValidationError>{t('clientForm.redirectUriRequired')}</ValidationError>,
                          },
                          {
                            pattern: redirectUriPattern,
                            message: <ValidationError>{t('clientForm.redirectUriInvalid')}</ValidationError>,
                          },
                        ]}
                        noStyle
                      >
                        <Input
                          placeholder={t('clientForm.redirectUriPlaceholder')}
                          style={{ flex: 1, borderRadius: '4px', fontSize: '14px' }}
                        />
                      </Form.Item>
                      {fields.length > 1 ? (
                        <MinusCircleOutlined
                          style={{ fontSize: '16px', color: '#ff4d4f', marginTop: '8px' }}
                          onClick={() => remove(field.name)}
                        />
                      ) : null}
                    </div>
                  </Form.Item>
                ))}
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button type="dashed" onClick={() => add()} style={{ width: '100%' }} icon={<PlusOutlined />}>
                    {t('clientForm.addRedirectUri')}
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
                <span style={{ color: '#8c8c8c', fontSize: '13px' }}>{t('clientForm.redirectUrisHelp')}</span>
              </>
            )}
          </Form.List>
        </div>
      </Container>
    </Card>
  );
}
